import React, { useState, useEffect } from 'react';
import { ChevronRight, Command, Lightbulb, Cpu, Palette } from 'lucide-react';

export default function DeviceIntegration() {
  const [selectedSection, setSelectedSection] = useState('command-control');
  const [activeImage, setActiveImage] = useState('');
  const [fadeIn, setFadeIn] = useState(false);

  const sections = [
    {
      id: 'command-control',
      title: 'Command & Control',
      icon: <Command className="w-5 h-5" />,
      description: `Take control of your marketing operations from anywhere. Our unified command center gives you complete oversight of all campaigns, budgets, and performance metrics across every platform.`,
      image: '/Screenshot2025-03-1501.08.07.png',
      stats: {
        label: 'Active Campaigns',
        value: '2,500+'
      }
    },
    {
      id: 'creative-analytics',
      title: 'Creative Analytics',
      icon: <Palette className="w-5 h-5" />,
      description: `Track and analyze your creative assets with AI-powered insights. Monitor performance trends, get creative recommendations, and optimize your visual content across all platforms.

Key Features:
• Performance Trend Analysis
• Creative Recommendations
• Visual Score Analysis
• Engagement Rate Tracking
• A/B Testing Results
• Cross-Platform Creative Insights`,
      image: '/creativeperformance.png',
      stats: {
        label: 'Creatives Analyzed',
        value: '50K+'
      }
    },
    {
      id: 'strategic-copilot',
      title: 'Strategic AI Copilot',
      icon: <Cpu className="w-5 h-5" />,
      description: `Let our AI copilot analyze your campaigns and provide strategic recommendations to optimize your marketing performance.`,
      image: 'Screenshot2025-03-1501.05.17.png',
      stats: {
        label: 'Optimizations',
        value: '10K+'
      }
    }
  ];

  const getImageForSection = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    return section ? section.image : sections[0].image;
  };

  // Preload images için
  useEffect(() => {
    // Preload images to ensure smooth transitions
    sections.forEach(section => {
      const img = new Image();
      img.src = section.image;
    });
    
    // Set initial active image
    setActiveImage(getImageForSection(selectedSection));
  }, []);

  // Sekme değiştiğinde görsel geçişi için
  useEffect(() => {
    setFadeIn(false);
    const timer = setTimeout(() => {
      setActiveImage(getImageForSection(selectedSection));
      setFadeIn(true);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [selectedSection]);

  return (
    <div className="py-16 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center">
          <h2 className="text-4xl font-bold tracking-tight text-gray-900 mb-4">
            Your marketing command center
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Powerful analytics dashboard that brings all your marketing channels together in one unified, intelligent interface.
          </p>
        </div>

        {/* Navigation Tabs - Yeni tasarım */}
        <div className="flex justify-center mb-8">
          <div className="bg-white/90 backdrop-blur-sm rounded-full p-1.5 shadow-lg flex gap-2">
            {sections.map((section) => (
              <button
                key={section.id}
                className={`py-2.5 px-6 text-sm font-medium rounded-full transition-all duration-300 flex items-center gap-2 ${
                  selectedSection === section.id
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                onClick={() => setSelectedSection(section.id)}
              >
                {section.icon}
                {section.title}
              </button>
            ))}
          </div>
        </div>

        {/* Content Card - Yeni tasarım */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          <div className="flex flex-col md:flex-row">
            {/* Content Section */}
            <div className="md:w-4/12 p-8 md:border-r border-gray-100">
              <div
                key={selectedSection}
                className="transition-opacity duration-300"
                style={{animation: 'fadeIn 0.4s forwards'}}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    {sections.find(s => s.id === selectedSection).icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {sections.find(s => s.id === selectedSection).title}
                  </h3>
                </div>
                
                <p className="text-sm text-gray-600 whitespace-pre-line mb-6">
                  {sections.find(s => s.id === selectedSection).description}
                </p>

                {/* Stats Card */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 mb-6">
                  <p className="text-sm text-gray-600">
                    {sections.find(s => s.id === selectedSection).stats.label}
                  </p>
                  <p className="text-2xl font-bold text-blue-600">
                    {sections.find(s => s.id === selectedSection).stats.value}
                  </p>
                </div>

                <button className="group inline-flex items-center text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors">
                  {selectedSection === 'creative-analytics' ? 'Explore creative insights' : 'Learn more about this feature'}
                  <ChevronRight className="ml-1 w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>

            {/* Image Section */}
            <div className="md:w-8/12 relative">
              <div className="relative p-4 h-full flex items-center justify-center">
                <div className="relative w-full overflow-hidden rounded-xl bg-white shadow-2xl">
                  <img
                    src={activeImage}
                    alt={sections.find(s => s.id === selectedSection).title}
                    className="w-full h-full object-contain"
                  />
                  
                  {selectedSection === 'creative-analytics' && (
                    <>
                      <div className="absolute bottom-4 left-4 flex gap-4">
                        <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg text-xs font-medium text-gray-800 shadow-lg flex items-center gap-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span>Visual Score: 92%</span>
                        </div>
                        <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg text-xs font-medium text-gray-800 shadow-lg flex items-center gap-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span>Engagement Rate: +45%</span>
                        </div>
                      </div>
                      <div className="absolute top-4 right-4 bg-blue-500 text-white px-4 py-2 rounded-lg text-xs font-medium shadow-lg">
                        AI-Powered Creative Analysis
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom dots navigation */}
        <div className="mt-8 flex justify-center gap-2">
          {sections.map((section) => (
            <button
              key={section.id}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                selectedSection === section.id ? 'bg-blue-600 transform scale-125' : 'bg-gray-300 hover:bg-gray-400'
              }`}
              onClick={() => setSelectedSection(section.id)}
              aria-label={`Select ${section.title}`}
            />
          ))}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
} 